import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CollaborationMapOutline, DocumentReportOutline, rem, SmileyFaceOutline, Text, UserGroupOutline, VStack } from '@bonusly/design-system';
import { Permission, ProtectedContent, useFeatureFlag, usePermission, Variant } from '@bonusly/who-am-i';

import { Conceal } from '../../../../components/display/conceal';
import { Reveal } from '../../../../components/display/reveal';
import { Panel } from '../../../../components/panel';
import { AnalyticsEvent } from '../../../../lib/analytics/events';
import { useAnalytics } from '../../../../lib/analytics/use-analytics';
import { DirectReport } from '../../../../types/user';
import { Person } from '../../../bonus-feed/features/recognition/components/tracker-widget/types';
import { IconAndTextContent } from '../icon-and-text-content';
import { UserNavLink } from '../user-nav-link';
import { DirectReportMenu } from './direct-report-menu';
import { TeamTracker } from './team-tracker';

interface TeamPanelProps {
	directReports: DirectReport[];
	isOpen: boolean;
	isSidebarExpanded: boolean;
	onClose: () => void;
	people?: Person[];
}

export const TeamPanel: FC<TeamPanelProps> = ({ directReports, isOpen, isSidebarExpanded, onClose, people }) => {
	const { t } = useTranslation('navigation');
	const { track } = useAnalytics();
	const [isReady, setIsReady] = useState(false);
	const isRemoveHomepageWidgetColumnEnabled = useFeatureFlag(Variant.RemoveHomepageWidgetColumn);
	const hasAnalyticsTier = usePermission(Permission.CanAccessAnalytics);

	// Defer rendering children until after the panel has mounted
	// This prevents flicker caused by complex children triggering layout calculations
	useEffect(() => {
		let frameId: number | undefined;

		if (isOpen && !isReady) {
			// Use requestAnimationFrame to wait for the panel to paint first
			frameId = requestAnimationFrame(() => {
				setIsReady(true);
			});
		}
		if (!isOpen) {
			setIsReady(false);
		}

		return () => {
			if (frameId !== undefined) {
				cancelAnimationFrame(frameId);
			}
		};
	}, [isOpen, isReady]);

	return (
		<Panel
			isOpen={isOpen}
			onClose={onClose}
			isSidebarExpanded={isSidebarExpanded}
			title={t('teamMenu.title')}
			closeButtonLabel={t('teamMenu.closePanel')}
			width={rem(350)}
			data-testid="team-panel"
		>
			{isReady && (
				<>
					{/* TEAM HUB LINKS - MANAGERS ONLY */}
					<ProtectedContent permissions={Permission.CanAccessTeamHub}>
						<VStack id="team-menu-items" spacing={1}>
							{/* YOUR TEAM */}
							<UserNavLink
								to="/team/your-team"
								onClick={() => track(AnalyticsEvent.TeamPanel.LinkClicked, { link: 'Team Hub' })}
								data-testid="nav-link-your-team"
							>
								<IconAndTextContent icon={<UserGroupOutline size="md" />} label={t('teamMenu.teamHub')} isExpanded />
							</UserNavLink>

							{/* PULSE RESULTS */}
							<UserNavLink
								to="/team/pulse-results"
								onClick={() => track(AnalyticsEvent.TeamPanel.LinkClicked, { link: 'Pulse Results' })}
								data-testid="nav-link-pulse-results"
							>
								<IconAndTextContent icon={<SmileyFaceOutline size="md" />} label={t('teamMenu.pulseResults')} isExpanded />
							</UserNavLink>

							{/* TEAM REPORT */}
							<UserNavLink
								to="/team/team-report"
								onClick={() => track(AnalyticsEvent.TeamPanel.LinkClicked, { link: 'Team Report' })}
								data-testid="nav-link-team-report"
							>
								<IconAndTextContent icon={<DocumentReportOutline size="md" />} label={t('teamMenu.teamReport')} isExpanded />
							</UserNavLink>

							{/* COLLABORATION MAP */}
							{hasAnalyticsTier && (
								<UserNavLink
									to="/team/collaboration"
									onClick={() => track(AnalyticsEvent.TeamPanel.LinkClicked, { link: 'Collaboration Map' })}
									data-testid="nav-link-collaboration-map"
								>
									<IconAndTextContent icon={<CollaborationMapOutline size="md" />} label={t('teamMenu.collaborationMap')} isExpanded />
								</UserNavLink>
							)}
						</VStack>
					</ProtectedContent>

					{/* TRACKER - PROGRESS BAR + USER LIST */}
					<Reveal if={isRemoveHomepageWidgetColumnEnabled}>
						<TeamTracker directReports={directReports} people={people} />
					</Reveal>

					<Conceal if={isRemoveHomepageWidgetColumnEnabled}>
						{directReports.length > 0 && (
							<>
								<Text fontSize={100} fontWeight="medium" color="text.softest" textAlign="left" w="100%" px={2} mt={4} mb={2}>
									{t('teamMenu.directReportsNavLabel')}
								</Text>
								<VStack as="nav" aria-label={t('teamMenu.directReportsNavLabel')} gap={rem(4)} w="100%">
									{directReports.map((directReport) => (
										<DirectReportMenu key={directReport.id} directReport={directReport} />
									))}
								</VStack>
							</>
						)}
					</Conceal>
				</>
			)}
		</Panel>
	);
};
